// client/src/pages/auth/VerifyEmail.tsx
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader } from '@/components/ui/card'
import Loading from '@/components/Loading'
import { useSearchParams, useNavigate, Link } from 'react-router-dom'
import { useEffect, useRef, useState } from 'react'
import { toast } from 'sonner'
import { useVerifyEmailMutation } from '@/hooks/useAuth'
import { useActivatePlanMutation, type PlanKey } from '@/hooks/usePlans'

export default function VerifyEmail() {
  const [params] = useSearchParams()
  const token = params.get('token')
  const navigate = useNavigate()

  const { mutateAsync: verifyEmail } = useVerifyEmailMutation()
  const { mutateAsync: activatePlan } = useActivatePlanMutation()
  const [status, setStatus] = useState<'pending' | 'error'>('pending')
  const ranRef = useRef(false)

  useEffect(() => {
    if (!token || ranRef.current) return
    ranRef.current = true

    const run = async () => {
      try {
        await verifyEmail({ token })

        const plan = localStorage.getItem(
          'pendingPlanActivation'
        ) as PlanKey | null
        if (plan) {
          localStorage.removeItem('pendingPlanActivation')
          try {
            await activatePlan(plan)
            toast.success('Plan activé')
          } catch (e: any) {
            toast.error(
              e?.response?.data?.message ?? 'Activation du plan impossible'
            )
          }
        }

        toast.success('Email vérifié, vous êtes connecté.')
        navigate('/dashboard', { replace: true })
      } catch {
        setStatus('error')
      }
    }
    run()
  }, [token, verifyEmail, activatePlan, navigate])

  if (!token || status === 'error') {
    return (
      <div className='flex flex-row items-center justify-center h-[100vh] bg-gradient-to-br from-[hsl(0,0%,100%)] via-[hsl(140,13%,91%)] to-[hsl(144,80%,73%)]'>
        <Card className='w-[520px] shadow-2xl rounded-xl'>
          <CardHeader>
            <p className='font-semibold text-xl'>Lien invalide ou expiré</p>
          </CardHeader>
          <CardContent className='space-y-4'>
            <p className='text-muted-foreground'>
              Ce lien de vérification n'est plus valide. Vous pouvez en
              redemander un depuis la page de connexion.
            </p>
            <Button asChild>
              <Link to='/login'>Retour à la connexion</Link>
            </Button>
          </CardContent>
        </Card>
      </div>
    )
  }

  return (
    <div className='grid place-items-center h-[100vh]'>
      <Loading />
    </div>
  )
}
